import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  deriveClientKey,
  isWeakPin,
  normalizeJoinCode,
  rollName,
  type RolledName,
} from "@vocifuchs/shared";
import { api, ApiError } from "../lib/api.js";
import { auth } from "../copy/auth.js";
import { common } from "../copy/common.js";
import { errors } from "../copy/errors.js";
import { AuthLayout } from "../components/AuthLayout.js";
import { Banner, Button, Die, Field, inputClass } from "../components/ui.js";
import { ClassCodeField } from "../components/ClassCodeField.js";
import { useClassPreview, useCodeFromLink } from "../lib/join.js";
import { useSession, type SessionUser } from "../session.js";

/** How many collisions we absorb silently before giving up (docs/PROFILE.md §3). */
const REROLLS_ON_COLLISION = 5;

export function RegisterScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useSession();
  const joinMode = params.has("join");
  const [name, setName] = useState<RolledName>(() => rollName());
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  // A class link carries the code in the fragment (issue #48).
  const [joinCode, setJoinCode] = useCodeFromLink();
  const preview = useClassPreview(joinMode ? normalizeJoinCode(joinCode) : "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const create = async (candidate: RolledName, code: string, attempt: number): Promise<void> => {
    const dk = await deriveClientKey(pin, candidate.handle);
    try {
      const data = await api<{ user: SessionUser; recovery_code: string }>("/api/auth/register", {
        method: "POST",
        body: JSON.stringify({
          display_name: candidate.display,
          handle: candidate.handle,
          dk,
          ...(code ? { join_code: code } : {}),
        }),
      });
      if (code) {
        await refresh();
        navigate("/", { replace: true });
        return;
      }
      navigate("/recovery", { replace: true, state: { code: data.recovery_code, user: data.user } });
    } catch (err) {
      // Someone already has this name. The child never chose it, so there is
      // nothing to explain -- roll another one and carry on.
      if (err instanceof ApiError && err.code === "handle_taken" && attempt < REROLLS_ON_COLLISION) {
        const next = rollName();
        setName(next);
        return create(next, code, attempt + 1);
      }
      throw err;
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (isWeakPin(pin)) {
      setError(errors.weakPin);
      return;
    }
    if (pin !== pin2) {
      setError(errors.pinMismatch);
      return;
    }
    const code = normalizeJoinCode(joinCode);
    if (joinMode && code.length !== 8) {
      setError(errors.joinCodeLength);
      return;
    }
    // Join only a class the child has seen the name of (issue #42).
    if (joinMode && preview.state !== "found") {
      if (preview.state === "unknown") setError(preview.message);
      return;
    }
    setBusy(true);
    try {
      await create(name, code, 0);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : errors.failed);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      mood="think"
      title={joinMode ? auth.register.titleJoin : auth.register.titleSolo}
      intro={
        joinMode
          ? auth.register.introJoin
          : auth.register.introSolo
      }
      footer={
        <p className="text-center text-sm lg:text-left">
          <Link to="/login" className="font-semibold text-fox-ink underline-offset-4 hover:underline">
            {auth.welcome.alreadyHaveAccount}
          </Link>
        </p>
      }
    >
      <form className="space-y-4" onSubmit={(e) => void submit(e)}>
        {error ? <Banner>{error}</Banner> : null}
        {joinMode ? (
          <ClassCodeField
            label={auth.register.classCode}
            hint={auth.register.classCodeHint}
            value={joinCode}
            onChange={setJoinCode}
            preview={preview}
          />
        ) : null}

        <div className="space-y-2">
          <span className="text-sm font-semibold text-ink-soft">{auth.register.yourName}</span>
          {/* Exactly the compound the child will type, never a spaced-out
              variant -- but with a break opportunity between the words, so a
              long one wraps on a narrow phone instead of shrinking away
              (docs/PROFILE.md §8). The inner capitals carry the reading. */}
          <div className="flex items-stretch gap-2">
            <p className="min-w-0 flex-1 rounded-2xl border-2 border-dashed border-fox/40 bg-paper-2 px-4 py-4 font-display text-2xl leading-tight sm:text-3xl">
              {name.parts.map((part, index) => (
                <span key={part + String(index)}>
                  {part}
                  {index < name.parts.length - 1 ? <wbr /> : null}
                </span>
              ))}
            </p>
            <button
              type="button"
              disabled={busy}
              onClick={() => setName(rollName())}
              aria-label={auth.register.rerollAria}
              title={auth.register.rerollAria}
              className="flex w-14 shrink-0 items-center justify-center rounded-2xl border-2 border-line bg-paper-3/60 text-ink-soft transition-[color,border-color,transform] hover:border-fox/50 hover:text-fox active:translate-y-0.5 disabled:opacity-50 sm:w-16"
            >
              <Die className="h-7 w-7 sm:h-8 sm:w-8" />
            </button>
          </div>
          <p className="text-sm text-ink-soft">
            {auth.register.nameHint}
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={auth.register.pin}>
            <input
              className={inputClass()}
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              autoComplete="new-password"
              required
            />
          </Field>
          <Field label={auth.register.pinAgain}>
            <input
              className={inputClass()}
              type="password"
              value={pin2}
              onChange={(e) => setPin2(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              autoComplete="new-password"
              required
            />
          </Field>
        </div>
        <Button type="submit" disabled={busy || (joinMode && preview.state !== "found")}>
          {busy ? common.loading : auth.register.submit}
        </Button>
      </form>
    </AuthLayout>
  );
}
